import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Badge } from './ui/badge';
import {
  SkipForward,
  ChevronRight,
  ChevronLeft,
  Check,
  Sparkles,
  HelpCircle,
} from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { cn } from '../lib/utils';

export interface WizardStep {
  id: string;
  title: string;
  description: string;
  content: React.ReactNode;
  targetSelector?: string; // CSS selector for highlighting element
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface FeatureWizardProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  steps: WizardStep[];
  featureId: string; // Unique identifier for the feature
  showOnFirstVisit?: boolean; // Whether to show automatically on first visit
  onComplete?: () => void;
  onSkip?: () => void;
}

const FeatureWizard: React.FC<FeatureWizardProps> = ({
  open,
  onClose,
  title,
  description,
  steps,
  featureId,
  showOnFirstVisit = false,
  onComplete,
  onSkip,
}) => {
  const { translate } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [hasSeenFeature, setHasSeenFeature] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check if user has seen this feature before
  useEffect(() => {
    const seenFeatures = JSON.parse(
      localStorage.getItem('seenFeatures') || '{}'
    );
    setHasSeenFeature(!!seenFeatures[featureId]);
  }, [featureId]);

  // Auto-show on first visit if enabled
  useEffect(() => {
    if (showOnFirstVisit && !hasSeenFeature && !open) {
      // This would be triggered by the parent component
    }
  }, [showOnFirstVisit, hasSeenFeature, open]);

  const handleNext = () => {
    const newCompletedSteps = new Set(completedSteps);
    newCompletedSteps.add(activeStep);
    setCompletedSteps(newCompletedSteps);

    if (activeStep === steps.length - 1) {
      handleComplete();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSkip = () => {
    // Mark feature as seen
    const seenFeatures = JSON.parse(
      localStorage.getItem('seenFeatures') || '{}'
    );
    seenFeatures[featureId] = true;
    localStorage.setItem('seenFeatures', JSON.stringify(seenFeatures));

    onSkip?.();
    onClose();
  };

  const handleComplete = () => {
    // Mark feature as seen
    const seenFeatures = JSON.parse(
      localStorage.getItem('seenFeatures') || '{}'
    );
    seenFeatures[featureId] = true;
    localStorage.setItem('seenFeatures', JSON.stringify(seenFeatures));

    onComplete?.();
    onClose();
  };

  const handleStepClick = (stepIndex: number) => {
    setActiveStep(stepIndex);
  };

  const isStepCompleted = (stepIndex: number) => {
    return completedSteps.has(stepIndex);
  };

  const isStepOptional = (stepIndex: number) => {
    return steps[stepIndex].action !== undefined;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={cn('max-w-4xl w-full', isMobile && 'h-screen max-h-screen')}>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <div>
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription>{description}</DialogDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-primary">
                {activeStep + 1}/{steps.length}
              </Badge>
            </div>
          </div>
        </DialogHeader>

        <div
          className={cn(
            'flex gap-4',
            isMobile ? 'flex-col' : 'flex-row',
            'min-h-[400px]'
          )}
        >
          {/* Stepper */}
          <div
            className={cn(
              'flex-shrink-0 border-r',
              isMobile
                ? 'w-full border-r-0 border-b pb-4'
                : 'w-64 pr-4'
            )}
          >
            <div
              className={cn(
                'flex',
                isMobile ? 'flex-row overflow-x-auto gap-2' : 'flex-col gap-4'
              )}
            >
              {steps.map((step, index) => {
                const isActive = index === activeStep;
                const isCompleted = isStepCompleted(index);
                const isOptional = isStepOptional(index);

                return (
                  <div
                    key={step.id}
                    className={cn(
                      'flex items-start gap-3 cursor-pointer rounded-md p-2 transition-colors',
                      isActive && 'bg-accent',
                      !isMobile && 'hover:bg-accent/50',
                      isMobile && 'flex-shrink-0 min-w-[100px]'
                    )}
                    onClick={() => handleStepClick(index)}
                  >
                    <div
                      className={cn(
                        'flex items-center justify-center w-8 h-8 rounded-full border-2 flex-shrink-0',
                        isCompleted
                          ? 'bg-primary border-primary text-primary-foreground'
                          : isActive
                          ? 'border-primary text-primary'
                          : 'border-muted-foreground text-muted-foreground'
                      )}
                    >
                      {isCompleted ? (
                        <Check className="h-4 w-4" />
                      ) : isOptional ? (
                        <Sparkles className="h-4 w-4" />
                      ) : (
                        <span className="text-xs font-medium">{index + 1}</span>
                      )}
                    </div>
                    {!isMobile && (
                      <div className="flex-1 min-w-0">
                        <p
                          className={cn(
                            'text-sm font-medium',
                            isActive && 'text-foreground',
                            !isActive && 'text-muted-foreground'
                          )}
                        >
                          {step.title}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {step.description}
                        </p>
                        {isOptional && (
                          <Badge variant="outline" className="mt-1 text-xs">
                            Optional
                          </Badge>
                        )}
                      </div>
                    )}
                    {isMobile && (
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">
                          {step.title.split(' ')[0]}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-auto mb-4">
              <h3 className="text-lg font-semibold mb-2">
                {steps[activeStep].title}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {steps[activeStep].description}
              </p>
              <div className="mt-4">{steps[activeStep].content}</div>
              {steps[activeStep].action && (
                <div className="mt-4">
                  <Button
                    variant="outline"
                    onClick={steps[activeStep].action!.onClick}
                    className="w-full sm:w-auto"
                  >
                    <HelpCircle className="h-4 w-4 mr-2" />
                    {steps[activeStep].action!.label}
                  </Button>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div
              className={cn(
                'flex gap-2 pt-4 border-t',
                isMobile ? 'flex-col' : 'justify-between items-center'
              )}
            >
              <Button
                onClick={handleSkip}
                variant="ghost"
                className={cn(isMobile && 'w-full', isMobile && 'order-3')}
              >
                <SkipForward className="h-4 w-4 mr-2" />
                {translate('pos.common.action.skip')}
              </Button>

              <div
                className={cn(
                  'flex gap-2',
                  isMobile && 'w-full order-1',
                  isMobile && '[&>button]:flex-1'
                )}
              >
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  variant="outline"
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  {isMobile
                    ? translate('pos.common.action.back')
                    : translate('pos.common.action.previous')}
                </Button>
                <Button onClick={handleNext}>
                  {activeStep === steps.length - 1
                    ? translate('pos.common.action.finish')
                    : translate('pos.common.action.next')}
                  {activeStep === steps.length - 1 ? (
                    <Check className="h-4 w-4 ml-2" />
                  ) : (
                    <ChevronRight className="h-4 w-4 ml-2" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeatureWizard;
